import React, { useMemo, useRef, useState } from "react";
import FieldHelp from "../help/FieldHelp";
import {
  DEFAULT_RATES,
  MAX_AUTONOMY_HOURS,
  MIN_AUTONOMY_HOURS,
  NIGHT_WINDOW_HOURS,
  SQFT_PER_KWP,
  annualGridCost,
  buildSystem,
  effectiveGridRate,
  estimateCapex,
  formatPaybackYears,
  formatPkr,
  paybackYears,
  ratesAreCustomised,
  solarAreaSqFt,
} from "../utils/offGridPlanner";

/**
 * Decision-support panels for the Off-Grid Calculator: autonomy slider, CAPEX &
 * ROI, roof-space note, sizing rationale (worst-day chart) and the live
 * selection bar. Maths lives in utils/offGridPlanner.js; these only present it.
 */

const ACCENT = "#1ba08e";
const INK = "#0f172a";
const INK_2 = "#475569";
const INK_MUTED = "#94a3b8";
const GRID = "#e2e8f0";

const fmt = (value, digits = 1) =>
  Number(value).toLocaleString(undefined, { maximumFractionDigits: digits });

export const fmtSqFt = (sqft) => `${fmt(sqft, 0)} sq ft`;

export const spaceDetail = (solarWp) =>
  `Requires approx. ${fmtSqFt(solarAreaSqFt(solarWp))} of unshaded roof space (≈ ${fmt(SQFT_PER_KWP, 0)} sq ft per kWp)`;

export const autonomyDetail = (hours) =>
  hours === NIGHT_WINDOW_HOURS
    ? "Sized for your worst night (6 PM – 6 AM)"
    : `Sized to run ${hours} h on your worst night's average load`;

// "2025-07-14" -> "14 July 2025", read as a local date so no timezone can slip it a day.
const formatDay = (iso) => {
  const [y, m, d] = String(iso).split("-").map(Number);
  if (!y || !m || !d) return String(iso);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const hourLabel = (h) => `${String(h).padStart(2, "0")}:00`;

/* -------------------------------------------------------------------------- */
/* Autonomy                                                                    */
/* -------------------------------------------------------------------------- */

const AUTONOMY_PRESETS = [
  { hours: 2, label: "2 h", note: "critical shutdown" },
  { hours: 6, label: "6 h", note: "" },
  { hours: NIGHT_WINDOW_HOURS, label: "12 h", note: "full night" },
  { hours: MAX_AUTONOMY_HOURS, label: "24 h", note: "" },
];

export const AutonomyControl = ({ hours, onChange }) => (
  <div style={ps.autonomy}>
    <div style={ps.rowBetween}>
      <label htmlFor="offgrid-autonomy" style={ps.autonomyLabel}>
        <FieldHelp id="offGrid.autonomy">Battery backup wanted</FieldHelp>
      </label>
      <strong style={ps.autonomyValue}>{hours} h</strong>
    </div>
    <input
      id="offgrid-autonomy"
      type="range"
      min={MIN_AUTONOMY_HOURS}
      max={MAX_AUTONOMY_HOURS}
      step={1}
      value={hours}
      onChange={(e) => onChange(Number(e.target.value))}
      aria-valuetext={`${hours} hours`}
      style={{ width: "100%", accentColor: ACCENT }}
    />
    <div style={ps.presetRow}>
      {AUTONOMY_PRESETS.map((p) => (
        <button
          key={p.hours}
          type="button"
          onClick={() => onChange(p.hours)}
          style={{ ...ps.preset, ...(hours === p.hours ? ps.presetActive : {}) }}
        >
          {p.label}
          {p.note ? <span style={ps.presetNote}> · {p.note}</span> : null}
        </button>
      ))}
    </div>
    {hours > NIGHT_WINDOW_HOURS && (
      <p style={ps.hint}>
        Beyond {NIGHT_WINDOW_HOURS} h this assumes your worst night's average load keeps going.
      </p>
    )}
  </div>
);

/* -------------------------------------------------------------------------- */
/* CAPEX & ROI                                                                 */
/* -------------------------------------------------------------------------- */

const RATE_FIELDS = [
  { key: "solarPerKwp", label: "Solar panels (PKR per kWp)" },
  { key: "batteryPerKwh", label: "Battery (PKR per kWh)" },
  { key: "inverterPerKw", label: "Inverter (PKR per kW)" },
  { key: "gridRate", label: "Grid rate (PKR per kWh)", optional: true },
];

const RatesSettings = ({ rates, accountGridRate, onSave, onReset, onClose }) => {
  const [draft, setDraft] = useState(() => ({ ...rates }));

  const parsed = RATE_FIELDS.map((f) => {
    const raw = draft[f.key];
    if (f.optional && (raw === "" || raw == null)) return { ...f, ok: true, value: "" };
    const n = Number(raw);
    return { ...f, ok: Number.isFinite(n) && n > 0, value: n };
  });
  const valid = parsed.every((f) => f.ok);

  const save = () => {
    const next = {};
    parsed.forEach((f) => {
      next[f.key] = f.value;
    });
    onSave(next);
    onClose();
  };

  return (
    <div style={ps.settings} role="group" aria-label="Cost assumptions">
      <div style={ps.settingsGrid}>
        {parsed.map((f) => (
          <label key={f.key} style={ps.field}>
            <span style={ps.fieldLabel}>{f.label}</span>
            <input
              type="number"
              min="0"
              inputMode="decimal"
              value={draft[f.key]}
              placeholder={f.optional && accountGridRate ? `Account rate: ${accountGridRate}` : ""}
              onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
              style={{ ...ps.input, ...(f.ok ? {} : ps.inputBad) }}
              aria-invalid={!f.ok}
            />
          </label>
        ))}
      </div>
      <p style={ps.hint}>
        Enter prices from your supplier's quote. The starting solar rate is for panels only, so add
        mounting, cabling and installation to the rates if you want them counted. Leave the grid rate blank to use the unit
        price from your account. Saved on this device.
      </p>
      <div style={ps.settingsActions}>
        <button type="button" style={ps.primaryBtn} disabled={!valid} onClick={save}>
          Save rates
        </button>
        <button
          type="button"
          style={ps.ghostBtn}
          onClick={() => {
            onReset();
            setDraft({ ...DEFAULT_RATES });
          }}
        >
          Reset to defaults
        </button>
        <button type="button" style={ps.ghostBtn} onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
};

export const CapexRoiCard = ({ system, loadAnalysis, rates, accountGridRate, onSaveRates, onResetRates }) => {
  const [showSettings, setShowSettings] = useState(false);

  const capex = useMemo(() => estimateCapex(system, rates), [system, rates]);
  const gridRate = effectiveGridRate(rates, accountGridRate);
  const annualWh = loadAnalysis?.annualEnergyWh;
  const hasAnnual = annualWh != null;
  const annualSaving = hasAnnual ? annualGridCost(annualWh, gridRate) : 0;
  const years = paybackYears(capex.total, annualSaving);
  const customised = ratesAreCustomised(rates);
  const dataDays = Number(loadAnalysis?.dataDays) || 0;

  let paybackText = formatPaybackYears(years);
  let paybackNote = null;
  if (!hasAnnual) {
    paybackText = "—";
    paybackNote = "Yearly usage isn't reported by the server yet, so payback can't be worked out.";
  } else if (!(gridRate > 0)) {
    paybackText = "—";
    paybackNote = "No grid rate is set. Open the gear and enter your PKR per kWh.";
  } else if (years == null) {
    paybackText = "—";
    paybackNote = "No yearly grid usage was recorded for these machines.";
  }

  return (
    <div style={ps.card} className="offgrid-capex-card">
      <div style={ps.rowBetween}>
        <h4 style={ps.cardTitle}>
          <FieldHelp id="offGrid.capexRoi">Estimated CAPEX &amp; ROI</FieldHelp>
        </h4>
        <div style={ps.cardTools}>
          {!customised && <span style={ps.badgeWarn}>Indicative rates</span>}
          <button
            type="button"
            style={{ ...ps.gear, ...(showSettings ? ps.gearOn : {}) }}
            onClick={() => setShowSettings((v) => !v)}
            aria-expanded={showSettings}
            aria-label="Edit cost assumptions"
            title="Edit cost assumptions"
          >
            ⚙
          </button>
        </div>
      </div>

      {showSettings && (
        <RatesSettings
          rates={rates}
          accountGridRate={accountGridRate}
          onSave={onSaveRates}
          onReset={onResetRates}
          onClose={() => setShowSettings(false)}
        />
      )}

      <div style={ps.statGrid}>
        <div style={ps.stat}>
          <span style={ps.statLabel}>Total CAPEX</span>
          <strong style={ps.statValue} title={`PKR ${Math.round(capex.total).toLocaleString()}`}>
            {formatPkr(capex.total)}
          </strong>
          <span style={ps.statSub}>
            Solar {formatPkr(capex.solar)} · Battery {formatPkr(capex.battery)} · Inverter{" "}
            {formatPkr(capex.inverter)}
          </span>
        </div>
        <div style={ps.stat}>
          <span style={ps.statLabel}>Grid bill avoided / year</span>
          <strong style={ps.statValue}>{hasAnnual && gridRate > 0 ? formatPkr(annualSaving) : "—"}</strong>
          <span style={ps.statSub}>
            {hasAnnual
              ? `${fmt(annualWh / 1000, 0)} kWh/yr × PKR ${fmt(gridRate, 2)}`
              : "Yearly usage not available"}
          </span>
        </div>
        <div style={ps.stat}>
          <span style={ps.statLabel}>Payback period</span>
          <strong style={{ ...ps.statValue, color: ACCENT }}>{paybackText ?? "—"}</strong>
          <span style={ps.statSub}>{paybackNote || "Simple payback: CAPEX ÷ yearly saving"}</span>
        </div>
      </div>

      <p style={ps.hint}>
        Assumes the system replaces all of the selected machines' grid energy. Excludes maintenance,
        battery replacement, financing and tariff changes.
        {dataDays > 0 && dataDays < 330
          ? ` Only ${dataDays} days of readings exist, so yearly usage is extrapolated.`
          : ""}
      </p>
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Sizing rationale: the worst day, drawn                                      */
/* -------------------------------------------------------------------------- */

const CHART = { w: 560, h: 190, l: 46, r: 14, t: 14, b: 28 };

const niceStep = (rawStep) => {
  const mag = 10 ** Math.floor(Math.log10(rawStep));
  const norm = rawStep / mag;
  const pick = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  return pick * mag;
};

const axisTick = (v) => (v >= 1000 ? `${fmt(v / 1000, 1)}K` : fmt(v, 0));

export const PeakDayChart = ({ hourlyKw }) => {
  const wrapRef = useRef(null);
  const [active, setActive] = useState(null);

  const values = useMemo(() => {
    const arr = Array.isArray(hourlyKw) ? hourlyKw : [];
    return Array.from({ length: 24 }, (_, h) => {
      const v = arr[h];
      return v == null || !Number.isFinite(Number(v)) ? null : Number(v);
    });
  }, [hourlyKw]);

  const known = values.filter((v) => v != null);
  const maxV = known.length ? Math.max(...known) : 0;
  const step = niceStep(Math.max(maxV, 1) / 4);
  const yMax = step * Math.max(1, Math.ceil(maxV / step));
  const ticks = Array.from({ length: Math.round(yMax / step) + 1 }, (_, i) => i * step);

  const { w, h, l, r, t, b } = CHART;
  const plotW = w - l - r;
  const plotH = h - t - b;
  const x = (hour) => l + (hour / 23) * plotW;
  const y = (v) => t + plotH - (v / yMax) * plotH;

  // Runs of consecutive readings; a gap in the data breaks the line instead of dropping to zero.
  const runs = useMemo(() => {
    const out = [];
    let cur = [];
    values.forEach((v, hr) => {
      if (v == null) {
        if (cur.length) out.push(cur);
        cur = [];
      } else {
        cur.push([hr, v]);
      }
    });
    if (cur.length) out.push(cur);
    return out;
  }, [values]);

  const peakHour = known.length ? values.indexOf(maxV) : null;

  const setFromPointer = (e) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect || !rect.width) return;
    const px = ((e.clientX - rect.left) / rect.width) * w;
    setActive(Math.min(23, Math.max(0, Math.round(((px - l) / plotW) * 23))));
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      setActive((a) => (a == null ? 0 : Math.min(23, a + 1)));
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      setActive((a) => (a == null ? 23 : Math.max(0, a - 1)));
    } else if (e.key === "Escape") {
      setActive(null);
    }
  };

  const activeVal = active != null ? values[active] : null;

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <svg
        viewBox={`0 0 ${w} ${h}`}
        style={{ width: "100%", height: "auto", display: "block", touchAction: "pan-y" }}
        role="img"
        aria-label={`Load over the worst day, hour by hour. Highest reading ${fmt(maxV, 0)} kilowatts.`}
        tabIndex={0}
        onPointerMove={setFromPointer}
        onPointerLeave={() => setActive(null)}
        onKeyDown={onKeyDown}
        onBlur={() => setActive(null)}
      >
        {/* The battery is sized from the 6 PM - 6 AM window, so mark it. */}
        <rect x={x(0)} y={t} width={x(5) - x(0)} height={plotH} fill="#64748b" opacity="0.07" />
        <rect x={x(18)} y={t} width={x(23) - x(18)} height={plotH} fill="#64748b" opacity="0.07" />

        {ticks.map((tv) => (
          <g key={tv}>
            <line x1={l} x2={w - r} y1={y(tv)} y2={y(tv)} stroke={GRID} strokeWidth="1" />
            <text x={l - 8} y={y(tv) + 4} textAnchor="end" fontSize="11" fill={INK_MUTED}>
              {axisTick(tv)}
            </text>
          </g>
        ))}
        {[0, 6, 12, 18, 23].map((hr) => (
          <text key={hr} x={x(hr)} y={h - 8} textAnchor="middle" fontSize="11" fill={INK_MUTED}>
            {hourLabel(hr)}
          </text>
        ))}
        <text x={l - 8} y={t - 3} textAnchor="end" fontSize="11" fill={INK_MUTED}>
          kW
        </text>

        {runs.map((run, i) =>
          run.length === 1 ? (
            <circle key={i} cx={x(run[0][0])} cy={y(run[0][1])} r="4" fill={ACCENT} stroke="#fff" strokeWidth="2" />
          ) : (
            <g key={i}>
              <path
                d={`M${x(run[0][0])},${y(0)} ${run.map(([hr, v]) => `L${x(hr)},${y(v)}`).join(" ")} L${x(run[run.length - 1][0])},${y(0)} Z`}
                fill={ACCENT}
                opacity="0.1"
              />
              <path
                d={run.map(([hr, v], j) => `${j ? "L" : "M"}${x(hr)},${y(v)}`).join(" ")}
                fill="none"
                stroke={ACCENT}
                strokeWidth="2"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            </g>
          ),
        )}

        {peakHour != null && (
          <circle cx={x(peakHour)} cy={y(maxV)} r="4" fill={ACCENT} stroke="#fff" strokeWidth="2" />
        )}

        {active != null && (
          <g pointerEvents="none">
            <line x1={x(active)} x2={x(active)} y1={t} y2={t + plotH} stroke={INK_MUTED} strokeWidth="1" />
            {activeVal != null && (
              <circle cx={x(active)} cy={y(activeVal)} r="4" fill={ACCENT} stroke="#fff" strokeWidth="2" />
            )}
          </g>
        )}
      </svg>

      {active != null && (
        <div
          style={{
            ...ps.tooltip,
            left: `${(x(active) / w) * 100}%`,
            transform: `translateX(${active > 16 ? "-105%" : "8px"})`,
          }}
          role="status"
        >
          <strong style={{ color: INK, fontSize: 13 }}>
            {activeVal != null ? `${fmt(activeVal, 1)} kW` : "No reading"}
          </strong>
          <span style={{ color: INK_2, fontSize: 11 }}>{hourLabel(active)}</span>
        </div>
      )}
    </div>
  );
};

const HourTable = ({ hourlyKw }) => (
  <table style={ps.table}>
    <thead>
      <tr>
        <th style={ps.th}>Hour</th>
        <th style={{ ...ps.th, textAlign: "right" }}>Load (kW)</th>
        <th style={ps.th}>Hour</th>
        <th style={{ ...ps.th, textAlign: "right" }}>Load (kW)</th>
      </tr>
    </thead>
    <tbody>
      {Array.from({ length: 12 }, (_, i) => (
        <tr key={i}>
          {[i, i + 12].map((hr) => (
            <React.Fragment key={hr}>
              <td style={ps.td}>{hourLabel(hr)}</td>
              <td style={{ ...ps.td, textAlign: "right" }}>
                {hourlyKw?.[hr] == null ? "—" : fmt(hourlyKw[hr], 1)}
              </td>
            </React.Fragment>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);

export const SizingRationale = ({ peakDay, loadAnalysis }) => {
  const [open, setOpen] = useState(false);
  const [asTable, setAsTable] = useState(false);

  if (!peakDay?.date) return null;

  const actualKwh = Number(peakDay.energyWh) / 1000;
  const sizedKwh = Number(loadAnalysis?.totalDailyEnergyWh) / 1000;
  const gapPct = sizedKwh > 0 ? ((sizedKwh - actualKwh) / sizedKwh) * 100 : 0;

  return (
    <div style={ps.card} className="offgrid-rationale">
      <div style={ps.rowBetween}>
        <h4 style={ps.cardTitle}>
          <FieldHelp id="offGrid.sizingRationale">Why this size?</FieldHelp>
        </h4>
        <button type="button" style={ps.linkBtn} onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          {open ? "Hide chart" : "View chart"}
        </button>
      </div>
      <p style={ps.rationaleLine}>
        Sized from your busiest day on record: <strong>{formatDay(peakDay.date)}</strong> — peak load{" "}
        <strong>{fmt(peakDay.peakKw, 0)} kW</strong>, <strong>{fmt(actualKwh, 0)} kWh</strong> used.
      </p>
      {gapPct >= 5 && (
        <p style={ps.hint}>
          Panels are sized for {fmt(sizedKwh, 0)} kWh/day, {fmt(gapPct, 0)}% more than that day,
          because each machine is sized on its own worst day and they didn't all peak together. That
          margin is where a smaller selection saves money.
        </p>
      )}

      {open && (
        <div style={{ marginTop: 12 }}>
          <div style={ps.rowBetween}>
            <span style={ps.statLabel}>Load by hour on {formatDay(peakDay.date)}</span>
            <button type="button" style={ps.linkBtn} onClick={() => setAsTable((v) => !v)}>
              {asTable ? "Show chart" : "Show table"}
            </button>
          </div>
          {asTable ? <HourTable hourlyKw={peakDay.hourlyKw} /> : <PeakDayChart hourlyKw={peakDay.hourlyKw} />}
          <p style={ps.hint}>
            Shaded hours (6 PM – 6 AM) are the night window the battery is sized on. Gaps mean the
            meter sent no reading that hour.
          </p>
        </div>
      )}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/* Live selection bar (Custom mode)                                            */
/* -------------------------------------------------------------------------- */

export const SelectionBudgetBar = ({ summary, status, constants, hours, rates }) => {
  const system = useMemo(
    () =>
      summary.known > 0 && constants
        ? buildSystem(summary, constants, hours)
        : null,
    [summary, constants, hours],
  );
  const capex = system ? estimateCapex(system, rates) : null;
  const partial = summary.known < summary.selected;

  return (
    <div style={ps.bar} role="status" aria-live="polite" className="offgrid-budget-bar">
      <div style={ps.barItem}>
        <span style={ps.barLabel}>Selected</span>
        <strong style={ps.barValue}>
          {summary.selected} machine{summary.selected !== 1 ? "s" : ""}
        </strong>
      </div>

      {summary.selected === 0 ? (
        <span style={{ ...ps.hint, margin: 0 }}>
          Click machines to add them. The peak load and cost update as you go.
        </span>
      ) : status === "loading" ? (
        <span style={{ ...ps.hint, margin: 0 }}>Loading each machine's historical load…</span>
      ) : status === "error" || !system ? (
        <span style={{ ...ps.hint, margin: 0 }}>
          Live totals unavailable. Press Analyze for the full result.
        </span>
      ) : (
        <>
          <div style={ps.barItem} title="Sum of each machine's highest hourly reading, the same basis the inverter is sized on. Machines rarely peak together, so the real combined peak is lower.">
            <span style={ps.barLabel}>
              <FieldHelp id="offGrid.selectionBudget">Total peak load</FieldHelp>
            </span>
            <strong style={ps.barValue}>{fmt(summary.peakW / 1000, 1)} kW</strong>
          </div>
          <div style={ps.barItem}>
            <span style={ps.barLabel}>Est. system</span>
            <strong style={ps.barValue}>
              {fmt(system.solarWp / 1000, 1)} kWp · {fmt(system.batteryWh / 1000, 1)} kWh ·{" "}
              {fmt(system.inverterW / 1000, 1)} kW
            </strong>
          </div>
          <div style={ps.barItem}>
            <span style={ps.barLabel}>Est. cost</span>
            <strong style={{ ...ps.barValue, color: ACCENT }} title={`PKR ${Math.round(capex.total).toLocaleString()}`}>
              {formatPkr(capex.total)}
            </strong>
          </div>
          {partial && (
            <span style={{ ...ps.hint, margin: 0 }}>
              {summary.known} of {summary.selected} have load data
            </span>
          )}
        </>
      )}
    </div>
  );
};

/* -------------------------------------------------------------------------- */

const ps = {
  rowBetween: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 },
  card: {
    marginTop: 18,
    padding: "16px 20px",
    background: "#fff",
    border: "1px solid rgba(27, 160, 142, 0.18)",
    borderRadius: 12,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
  },
  cardTitle: {
    margin: 0,
    fontSize: 13,
    fontWeight: 600,
    color: ACCENT,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  cardTools: { display: "flex", alignItems: "center", gap: 8 },
  badgeWarn: {
    fontSize: 11,
    fontWeight: 700,
    color: "#b45309",
    background: "#fffbeb",
    border: "1px solid #fde68a",
    borderRadius: 999,
    padding: "2px 10px",
  },
  gear: {
    width: 32,
    height: 32,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: "#e2e8f0",
    borderRadius: 8,
    background: "#f8fafc",
    color: INK_2,
    fontSize: 16,
    cursor: "pointer",
    lineHeight: 1,
  },
  gearOn: { background: "#ecfdf5", borderColor: ACCENT, color: ACCENT },
  statGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 16,
    marginTop: 14,
  },
  stat: { display: "flex", flexDirection: "column", gap: 4, minWidth: 0 },
  statLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  statValue: { fontSize: 22, fontWeight: 700, color: INK, letterSpacing: "-0.02em" },
  statSub: { fontSize: 12, color: "#64748b", lineHeight: 1.5 },
  hint: { margin: "10px 0 0", fontSize: 12, color: "#64748b", lineHeight: 1.6 },
  settings: {
    marginTop: 12,
    padding: 14,
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
  },
  settingsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 12,
  },
  field: { display: "flex", flexDirection: "column", gap: 4 },
  fieldLabel: { fontSize: 11, fontWeight: 600, color: "#64748b" },
  input: {
    padding: "8px 10px",
    borderRadius: 8,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: "#cbd5e1",
    fontSize: 14,
    color: "#334155",
    background: "#fff",
  },
  inputBad: { borderColor: "#dc2626", background: "#fef2f2" },
  settingsActions: { display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 },
  primaryBtn: {
    padding: "8px 16px",
    border: "none",
    borderRadius: 8,
    background: "linear-gradient(135deg, #1ba08e, #17a98b)",
    color: "#fff",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
  },
  ghostBtn: {
    padding: "8px 14px",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    background: "#fff",
    color: INK_2,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  linkBtn: {
    padding: 0,
    border: "none",
    background: "none",
    color: ACCENT,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  autonomy: {
    marginTop: 18,
    padding: "14px 20px",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
  },
  autonomyLabel: { fontSize: 12, fontWeight: 700, color: INK_2, textTransform: "uppercase", letterSpacing: "0.08em" },
  autonomyValue: { fontSize: 18, color: ACCENT },
  presetRow: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 },
  preset: {
    padding: "4px 12px",
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: "#e2e8f0",
    borderRadius: 999,
    background: "#fff",
    color: INK_2,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  presetActive: { borderColor: ACCENT, background: "#ecfdf5", color: "#0f766e" },
  presetNote: { fontWeight: 500, color: INK_MUTED },
  rationaleLine: { margin: "10px 0 0", fontSize: 14, color: INK_2, lineHeight: 1.6 },
  tooltip: {
    position: "absolute",
    top: 8,
    display: "flex",
    flexDirection: "column",
    gap: 2,
    padding: "6px 10px",
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    boxShadow: "0 4px 14px rgba(0, 0, 0, 0.1)",
    pointerEvents: "none",
    whiteSpace: "nowrap",
  },
  table: { width: "100%", borderCollapse: "collapse", marginTop: 8, fontSize: 12 },
  th: { textAlign: "left", padding: "4px 8px", color: "#64748b", borderBottom: `1px solid ${GRID}`, fontWeight: 600 },
  td: { padding: "3px 8px", color: INK_2, borderBottom: `1px solid ${GRID}`, fontVariantNumeric: "tabular-nums" },
  bar: {
    position: "sticky",
    bottom: 16,
    zIndex: 5,
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "10px 28px",
    padding: "12px 22px",
    background: "rgba(255, 255, 255, 0.96)",
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    border: "1px solid rgba(27, 160, 142, 0.35)",
    borderRadius: 16,
    boxShadow: "0 12px 32px -8px rgba(0, 0, 0, 0.18)",
  },
  barItem: { display: "flex", flexDirection: "column", gap: 2 },
  barLabel: { fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.08em" },
  barValue: { fontSize: 15, fontWeight: 700, color: INK },
};
